import { Box, Card, CheckBox, Text } from 'grommet';
import { Project, TUpdateProjectRequest } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { getAnimProps } from 'src/utils/styling';
import { TalonInput } from '..';
import { useProjectStore } from '../../../context';
import { EditableText } from '../EditableText';
import { FavoriteButton } from '../FavoriteButton';

export type ProjectDetailsPanelProps = {
  project: Project;
  headerBackground?: string;
};

export const ProjectDetailsPanel = observer(({ project, headerBackground }: ProjectDetailsPanelProps) => {
  /** Context **/
  const projectStore = useProjectStore();

  /** Methods **/

  const updateProject = async (update: TUpdateProjectRequest) => {
    if (!projectStore.currentProject) {
      toast.error('An internal error has occurred. Please refresh the page and try again.');
      return;
    }

    await projectStore.updateProject(projectStore.currentProject.id, update);
  };

  /** Render **/
  return (
    <Card margin="small">
      {/* Header */}
      <Box
        direction="row"
        gap="small"
        background={headerBackground || { color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)' }}
        pad={{ horizontal: 'small', vertical: 'medium' }}
        justify="between"
        align="center"
      >
        <Box direction="row" gap="small" animation={getAnimProps()}>
          <EditableText
            val={projectStore.currentProject?.name || ''}
            textColor="accent-1"
            onChange={(t) => updateProject({ name: t.target.value })}
          />
          <FavoriteButton project={project} iconProps={{ width: 'small' }} />
        </Box>
      </Box>

      {/* Form Inputs */}
      <Box direction="row-responsive" gap="medium" pad="small" background="light-1">
        <Box direction="column">
          <Box direction="row" animation={getAnimProps()}>
            <TalonInput
              label="Client"
              htmlFor="client-name"
              width="100%"
              val={projectStore.currentProject?.client?.name || ''}
              onChange={(name) => updateProject({ client: { name } })}
            />
          </Box>
          <Box direction="row" pad="xsmall" gap="small" animation={getAnimProps(0, 'slideUp')}>
            <CheckBox
              label={<Text weight={300}>SAG Signatory</Text>}
              checked={projectStore.currentProject?.client?.isSagSignatory}
              onChange={(e) => updateProject({ client: { isSagSignatory: e.target.checked } })}
            />
            <CheckBox
              label={<Text weight={300}>JPC Authorizer</Text>}
              checked={projectStore.currentProject?.client?.isJpcAuthorizer}
              onChange={(e) => updateProject({ client: { isJpcAuthorizer: e.target.checked } })}
            />
          </Box>
        </Box>
        <Box direction="row-responsive" animation={getAnimProps(50)}>
          <TalonInput
            val={(projectStore.currentProject?.talentBudgetInPennies || 0) / 100}
            label="Talent Budget"
            htmlFor="talent-budget"
            onChange={(budget: number) => updateProject({ talentBudgetInPennies: Math.round(budget * 100) })}
            width="100%"
            format="currency"
            currencyDecimals={false}
          />
        </Box>
        <Box direction="row-responsive" animation={getAnimProps(100)}>
          <TalonInput
            label="Product"
            htmlFor="product"
            width="100%"
            val={projectStore.currentProject?.product || ''}
            onChange={(product) => updateProject({ product })}
          />
        </Box>
        <Box direction="row-responsive" animation={getAnimProps(150)}>
          <TalonInput
            label="Job Code"
            htmlFor="job-code"
            width="100%"
            val={projectStore.currentProject?.job_code || ''}
            onChange={(jobCode) => updateProject({ jobCode })}
          />
        </Box>
        <Box direction="row-responsive" animation={getAnimProps(200)}>
          <TalonInput
            label="PO Number"
            htmlFor="po-number"
            width="100%"
            val={projectStore.currentProject?.poNumber || ''}
            onChange={(poNumber) => updateProject({ poNumber })}
          />
        </Box>
      </Box>

      {/* Project Timeline */}
      {/* <Box background="light-1" pad={{ horizontal: 'small', vertical: 'xsmall' }}>
        <ProjectTimeline />
      </Box> */}
    </Card>
  );
});
